import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../../lib/api";
import { usd } from "../../lib/affApi";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function ProgramSettings({ settings, reload }) {
  const [f, setF] = useState({ rate_pct: settings.rate_pct, min_payout: settings.min_payout, supplier_monthly_rate: settings.supplier_monthly_rate ?? "" });
  const [lastRun, setLastRun] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    const body = { rate_pct: Number(f.rate_pct), min_payout: Number(f.min_payout) };
    if (f.supplier_monthly_rate === "" || f.supplier_monthly_rate === null) body.clear_supplier_rate = true;
    else body.supplier_monthly_rate = Number(f.supplier_monthly_rate);
    try { await api.put("/admin/affiliates/settings", body); toast.success("Program settings saved"); reload(); } catch (e2) { toast.error(fmtErr(e2)); }
  };
  const runMonth = async () => {
    try { const { data } = await api.post("/admin/affiliates/run-month"); setLastRun(data); toast.success(`Accruals written for ${data.month}`); reload(); } catch (e2) { toast.error(fmtErr(e2)); }
  };
  return (
    <form onSubmit={save} className="rounded-xl border border-zinc-200 bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="aff-admin-settings">
      <div><label className="text-xs text-zinc-500">Commission %</label><Input type="number" step="0.5" min="0" max="100" value={f.rate_pct} onChange={(e) => setF({ ...f, rate_pct: e.target.value })} data-testid="aff-admin-rate" /></div>
      <div><label className="text-xs text-zinc-500">Minimum payout (USD)</label><Input type="number" min="0" value={f.min_payout} onChange={(e) => setF({ ...f, min_payout: e.target.value })} data-testid="aff-admin-min-payout" /></div>
      <div><label className="text-xs text-zinc-500">Supplier monthly rate (USD, blank = not yet)</label><Input type="number" min="0" value={f.supplier_monthly_rate} onChange={(e) => setF({ ...f, supplier_monthly_rate: e.target.value })} data-testid="aff-admin-supplier-rate" /></div>
      <div className="flex gap-2">
        <Button type="submit" className="bg-ink hover:bg-black" data-testid="aff-admin-settings-save">Save</Button>
        <Button type="button" variant="outline" onClick={runMonth} data-testid="aff-admin-run-month">Run monthly accrual now</Button>
      </div>
      {lastRun && <div className="md:col-span-4 text-xs text-zinc-500" data-testid="aff-admin-run-result">Last run: {lastRun.month} — {lastRun.accruals_written} accruals, {lastRun.payouts_created} payouts created.</div>}
    </form>
  );
}

function AffiliatesList({ rows, reload }) {
  const patch = async (id, body) => {
    try { await api.patch(`/admin/affiliates/${id}`, body); reload(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const setRate = (a) => {
    const v = window.prompt(`Per-affiliate commission % for ${a.name} (blank = use global rate)`, a.rate_override ?? "");
    if (v === null) return;
    patch(a.id, v.trim() === "" ? { clear_rate_override: true } : { rate_override: Number(v) });
  };
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto" data-testid="aff-admin-list">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs text-zinc-500"><tr>{["Affiliate", "Code", "Status", "Rate", "Clicks", "Sign-ups", "Spend", "Owed", "Paid", ""].map((h) => <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-zinc-500">No affiliates yet.</td></tr>}
          {rows.map((a) => (
            <tr key={a.id} data-testid={`aff-admin-row-${a.id}`}>
              <td className="px-3 py-2"><div className="font-medium">{a.name}</div><div className="text-xs text-zinc-500">{a.email}{a.company ? ` · ${a.company}` : ""}</div></td>
              <td className="px-3 py-2 font-mono text-xs">{a.code}</td>
              <td className="px-3 py-2"><Badge variant="outline" className={a.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{a.status}</Badge></td>
              <td className="px-3 py-2">{a.rate_pct}%{a.rate_override != null && <span className="text-xs text-zinc-400"> (override)</span>}</td>
              <td className="px-3 py-2">{a.clicks}</td>
              <td className="px-3 py-2">{a.signups}</td>
              <td className="px-3 py-2">{usd(a.spend)}</td>
              <td className="px-3 py-2 font-medium">{usd(a.commission_owed)}</td>
              <td className="px-3 py-2">{usd(a.commission_paid)}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                <button className="text-xs underline mr-3" onClick={() => setRate(a)} data-testid={`aff-admin-rate-${a.id}`}>Set rate</button>
                <button className="text-xs underline" onClick={() => patch(a.id, { status: a.status === "active" ? "suspended" : "active" })} data-testid={`aff-admin-toggle-${a.id}`}>{a.status === "active" ? "Suspend" : "Reactivate"}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReferralsList({ rows, reload }) {
  const detach = async (r) => {
    if (!window.confirm(`Detach ${r.customer_email} from ${r.affiliate_name}? The affiliate stops earning on this customer.`)) return;
    try { await api.post(`/admin/affiliates/referrals/${r.id}/detach`); toast.success("Referral detached"); reload(); } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto" data-testid="aff-admin-referrals">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs text-zinc-500"><tr>{["Customer", "Type", "Affiliate", "Signed up", "Trial ends", "Status", "Yachts", "This month", ""].map((h) => <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-zinc-500">No referrals yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className={r.detached ? "opacity-50" : ""} data-testid={`aff-admin-ref-${r.id}`}>
              <td className="px-3 py-2"><div className="font-medium">{r.customer_name || "—"}</div><div className="text-xs text-zinc-500">{r.customer_email || r.user_id} · #{String(r.customer_no).padStart(4, "0")}</div></td>
              <td className="px-3 py-2">{r.kind}</td>
              <td className="px-3 py-2">{r.affiliate_name} <span className="font-mono text-xs text-zinc-500">{r.affiliate_code}</span></td>
              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
              <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.trial_end)}</td>
              <td className="px-3 py-2">{r.status}</td>
              <td className="px-3 py-2">{r.yachts}</td>
              <td className="px-3 py-2">{usd(r.this_month_spend)}</td>
              <td className="px-3 py-2">{!r.detached && <button className="text-xs underline text-red-600" onClick={() => detach(r)} data-testid={`aff-admin-detach-${r.id}`}>Detach</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayoutsList({ rows, reload }) {
  const markPaid = async (p) => {
    const ref = window.prompt(`Payment reference for ${usd(p.amount)} to ${p.affiliate?.name}`);
    if (!ref) return;
    try { await api.post(`/admin/affiliates/payouts/${p.id}/paid`, { reference: ref }); toast.success("Marked as paid — affiliate notified"); reload(); } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto" data-testid="aff-admin-payouts">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs text-zinc-500"><tr>{["Created", "Affiliate", "Bank / PayPal", "Period", "Amount", "Status", "Reference", ""].map((h) => <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">No payouts yet — they're created on the 1st for balances above the minimum.</td></tr>}
          {rows.map((p) => {
            const po = p.affiliate?.payout || {};
            return (
              <tr key={p.id} data-testid={`aff-admin-payout-${p.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                <td className="px-3 py-2"><div className="font-medium">{p.affiliate?.name}</div><div className="text-xs text-zinc-500">{p.affiliate?.email}</div></td>
                <td className="px-3 py-2 text-xs text-zinc-600">{po.iban ? <>{po.holder} · {po.iban}{po.bic ? ` · ${po.bic}` : ""}</> : po.paypal_email ? `PayPal ${po.paypal_email}` : <span className="text-amber-700">No payout details yet</span>}</td>
                <td className="px-3 py-2">{p.period}</td>
                <td className="px-3 py-2 font-medium">{usd(p.amount)}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={p.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{p.status}</Badge></td>
                <td className="px-3 py-2 text-zinc-600">{p.reference || "—"}</td>
                <td className="px-3 py-2">{p.status === "pending" && <Button size="sm" onClick={() => markPaid(p)} data-testid={`aff-admin-mark-paid-${p.id}`}>Mark paid</Button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AffiliatesAdmin() {
  const [settings, setSettings] = useState(null);
  const [affs, setAffs] = useState([]);
  const [refs, setRefs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/affiliates/settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/admin/affiliates").then(({ data }) => setAffs(data)).catch(() => {});
    api.get("/admin/affiliates/referrals").then(({ data }) => setRefs(data)).catch(() => {});
    api.get("/admin/affiliates/payouts").then(({ data }) => setPayouts(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!settings) return <p className="text-sm text-zinc-500 mt-4">Loading…</p>;
  return (
    <div className="space-y-6 mt-4 px-4 md:px-0" data-testid="aff-admin">
      <section><h2 className="font-medium mb-2">Program settings</h2><ProgramSettings key={JSON.stringify(settings)} settings={settings} reload={load} /></section>
      <section><h2 className="font-medium mb-2">Affiliates ({affs.length})</h2><AffiliatesList rows={affs} reload={load} /></section>
      <section><h2 className="font-medium mb-2">Referrals ({refs.length})</h2><ReferralsList rows={refs} reload={load} /></section>
      <section><h2 className="font-medium mb-2">Payouts ({payouts.length})</h2><PayoutsList rows={payouts} reload={load} /></section>
    </div>
  );
}
